import { useEffect, useState } from "react";
import type { TranslateFn } from "../../i18n";
import { SparkleIcon, CameraIcon, ArrowLeftIcon } from "../icons";
import { useIsMobile } from "../utils/useIsMobile";
import { PhotoUploadZone } from "./PhotoUploadZone";

/**
 * Photo guide screen — mirrors the inline `showPhotoGuide` overlay inside
 * SizeResultView. Used for try-on flows that don't have a size result yet
 * (e.g. NoChartView's "See how it looks on you").
 *
 * Desktop: split — photo upload on the left, Do/Don't/Pro-Tip on the right.
 * Mobile: stacked — title + preview + checklist + START TRY-ON.
 */
export function PhotoGuideView({
	measurementType = "body",
	apiUrl,
	apiKey,
	onBack,
	onSubmit,
	t,
}: {
	measurementType?: "body" | "face" | "head" | "foot" | "wrist";
	/** Backend URL for the age-check call (passed through to PhotoUploadZone). */
	apiUrl?: string;
	apiKey?: string;
	onBack: () => void;
	onSubmit: (file: File) => void;
	t: TranslateFn;
}) {
	const isMobile = useIsMobile();
	const [guideFile, setGuideFile] = useState<File | null>(null);
	const [guidePreviewUrl, setGuidePreviewUrl] = useState<string | null>(null);
	useEffect(() => {
		if (!guideFile) {
			setGuidePreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(guideFile);
		setGuidePreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [guideFile]);

	const submit = () => {
		if (!guideFile) return;
		onSubmit(guideFile);
	};

	if (isMobile) {
		return (
			<div className="ps-bp-wrapper" style={{ padding: "16px 16px 0", background: "var(--ps-bg-primary)" }}>
				<div className="ps-pm-root">
					<div className="ps-pm-header">
						<h2 className="ps-pm-title">{t("Review your photo")}</h2>
						<p className="ps-pm-subtitle">
							{measurementType === "face"
								? t("A clear, front-facing face photo — no glasses on — gives us the most accurate try-on.")
								: measurementType === "head"
								? t("Face the camera with your head and shoulders in frame, leaving space above your head.")
								: t("Ensure your full body is visible for the most accurate virtual try-on.")}
						</p>
					</div>

					<div className="ps-pm-preview">
						<PhotoUploadZone
							apiUrl={apiUrl}
							apiKey={apiKey}
							previewUrl={guidePreviewUrl}
							variant="inline"
							onPhotoAccepted={(f) => setGuideFile(f)}
							onClearPhoto={() => setGuideFile(null)}
							emptyTitle={t("Tap to upload")}
							t={t}
						/>
					</div>

					<div className="ps-pm-checklist">
						<div className="ps-pm-checklist-icon">
							<svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
								<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
							</svg>
						</div>
						<div className="ps-pm-checklist-body">
							<div className="ps-pm-checklist-title">{t("Checklist for accuracy")}</div>
							<ul className="ps-pm-checklist-items">
								{measurementType === "face" ? (
									<>
										<li>{t("Face the camera directly at eye level")}</li>
										<li>{t("Remove any glasses you're wearing")}</li>
										<li>{t("Good lighting, plain background")}</li>
									</>
								) : measurementType === "head" ? (
									<>
										<li>{t("Head and shoulders in frame")}</li>
										<li>{t("Leave space above your head")}</li>
										<li>{t("Good lighting, plain background")}</li>
									</>
								) : (
									<>
										<li>{t("Wear fitted, simple clothing")}</li>
										<li>{t("Standing 2-3 meters from camera")}</li>
										<li>{t("Neutral background with good lighting")}</li>
									</>
								)}
							</ul>
						</div>
					</div>
					<div className="ps-bpm-spacer" />

					<div className="ps-bpm-bottom">
						<button
							type="button"
							className="ps-pm-primary-btn"
							disabled={!guideFile}
							onClick={submit}
						>
							{t("START TRY-ON")}
						</button>
						{guideFile ? (
							<button
								type="button"
								className="ps-pm-secondary-btn"
								onClick={() => setGuideFile(null)}
							>
								{t("RETAKE PHOTO")}
							</button>
						) : (
							<button
								type="button"
								className="ps-pm-secondary-btn"
								onClick={onBack}
							>
								{t("BACK")}
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", padding: "1.5vw", width: "100%", height: "100%", background: "var(--ps-bg-primary)", borderRadius: "0.8vw", overflow: "hidden" }}>
			<div style={{ display: "flex", gap: "1.2vw", flex: 1, alignItems: "stretch", minHeight: 0, overflow: "hidden" }}>
				{/* Left: Upload area — unified component handles drag/drop + age check + rejection. */}
				<div style={{ flex: 1, display: "flex", minHeight: 0 }}>
					<PhotoUploadZone
						apiUrl={apiUrl}
						apiKey={apiKey}
						previewUrl={guidePreviewUrl}
						variant="inline"
						emptyTitle={t("Upload your photo")}
						hintLine={
							measurementType === "face"
								? t("Click or drag a close-up face photo")
								: measurementType === "head"
								? t("Click or drag a head-and-shoulders photo")
								: t("Click or drag a full-body photo")
						}
						onPhotoAccepted={(f) => setGuideFile(f)}
						onClearPhoto={() => setGuideFile(null)}
						t={t}
					/>
				</div>

				{/* Right: Guidelines */}
				<div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.6vw" }}>
					<div style={{ fontSize: "0.85vw", fontWeight: 700, color: "var(--ps-text-primary)", marginBottom: "0.3vw" }}>{t("How to take the best photo")}</div>
					<div style={{ background: "#ddfbe7", borderRadius: "0.5vw", padding: "0.6vw 0.8vw" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
							<span style={{ color: "#1c9d4c", fontSize: "0.75vw", fontWeight: 700 }}>✓</span>
							<span style={{ color: "#1c9d4c", fontSize: "0.65vw", fontWeight: 600 }}>{t("Do")}</span>
						</div>
						<div style={{ fontSize: "0.58vw", color: "var(--ps-text-primary)", lineHeight: 1.8 }}>
							{measurementType === "face" ? (
								<>
									{t("Face the camera directly, centered in frame")}<br/>
									{t("Use natural, even lighting (e.g. near a window)")}<br/>
									{t("Keep hair away from your face and ears")}<br/>
									{t("Choose a plain, light background")}
								</>
							) : measurementType === "head" ? (
								<>
									{t("Face the camera with head and shoulders in frame")}<br/>
									{t("Leave some space above your head")}<br/>
									{t("Use natural, even lighting")}<br/>
									{t("Choose a plain, light background")}
								</>
							) : (
								<>
									{t("Stand facing the camera with your full body in frame")}<br/>
									{t("Use natural or even lighting")}<br/>
									{t("Wear fitted, simple clothing")}<br/>
									{t("Stand straight and still, arms relaxed")}
								</>
							)}
						</div>
					</div>
					<div style={{ background: "#ffe2e2", borderRadius: "0.5vw", padding: "0.6vw 0.8vw" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
							<span style={{ color: "#e7000b", fontSize: "0.75vw", fontWeight: 700 }}>✗</span>
							<span style={{ color: "#e7000b", fontSize: "0.65vw", fontWeight: 600 }}>{t("Don't")}</span>
						</div>
						<div style={{ fontSize: "0.58vw", color: "var(--ps-text-primary)", lineHeight: 1.8 }}>
							{measurementType === "face" ? (
								<>
									{t("Don't wear sunglasses or a hat in the photo")}<br/>
									{t("Don't tilt or turn your head")}<br/>
									{t("Don't use strong backlighting or flash")}<br/>
									{t("Don't apply filters or edits")}
								</>
							) : measurementType === "head" ? (
								<>
									{t("Don't wear a hat in the photo")}<br/>
									{t("Don't crop out the top of your head")}<br/>
									{t("Don't use strong backlighting or flash")}<br/>
									{t("Don't apply filters or edits")}
								</>
							) : (
								<>
									{t("Don't wear loose or baggy clothing")}<br/>
									{t("Don't sit, pose, or bend")}<br/>
									{t("Don't take mirror photos or selfies")}<br/>
									{t("Don't apply filters or edits")}
								</>
							)}
						</div>
					</div>
					<div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "0.5vw", padding: "0.5vw 0.8vw" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.2vw" }}>
							<SparkleIcon size={12} />
							<span style={{ color: "var(--ps-accent)", fontSize: "0.65vw", fontWeight: 700 }}>{t("Pro Tip")}</span>
						</div>
						<div style={{ fontSize: "0.55vw", color: "var(--ps-text-secondary)", lineHeight: 1.7 }}>
							{measurementType === "face"
								? t("A clear, well-lit face photo gives the most accurate eyewear try-on.")
								: measurementType === "head"
								? t("A clear head-and-shoulders photo with space above your head gives the most accurate headwear try-on.")
								: t("Our AI works best with front-facing, full-body photos in fitted clothing. Better photos = more accurate virtual try-on!")}
						</div>
					</div>
				</div>
			</div>

				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.8vw", gap: "0.5vw", flexShrink: 0 }}>
					<button className="ps-bp-back-btn" onClick={onBack} type="button">
						<span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
					</button>
					<button className="ps-tryon-v2-cta" style={{ marginTop: 0 }} disabled={!guideFile} onClick={submit}>
						<CameraIcon size={14} /> {t("Start Try-On")}
				</button>
			</div>
		</div>
	);
}
